export type HelpSectionKey =
  | "start"
  | "vertrieb"
  | "kunden"
  | "ansprechpartner"
  | "angebote"
  | "kommunikation"
  | "dokumente"
  | "dateiexplorer"
  | "aktivitaeten"
  | "projekte"
  | "finanzen"
  | "marketing"
  | "lernen"
  | "befragungen"
  | "bem"
  | "wissen"
  | "berichte"
  | "verwaltung";

export const helpGroups = [
  "Einstieg",
  "Kunden und Vertrieb",
  "Zusammenarbeiten",
  "Auswerten und verwalten",
] as const;

export type HelpSection = {
  key: HelpSectionKey;
  label: string;
  description: string;
  group: (typeof helpGroups)[number];
  feature?: string;
};

export const helpSections: readonly HelpSection[] = [
  {
    key: "start",
    label: "Erste Schritte",
    description: "Suchen und zurechtfinden",
    group: "Einstieg",
  },
  {
    key: "vertrieb",
    label: "Vertrieb",
    description: "Anfragen und nächste Schritte",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "kunden",
    label: "Kunden",
    description: "Unternehmen und Kundenakten",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "ansprechpartner",
    label: "Ansprechpartner",
    description: "Kontakte bei Kunden",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "angebote",
    label: "Angebote",
    description: "Lexware-Angebote und Aufträge",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "kommunikation",
    label: "Kommunikation",
    description: "E-Mails und Gespräche",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "dokumente",
    label: "Dokumente",
    description: "Unterlagen beim Kunden",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "aktivitaeten",
    label: "Aktivitäten",
    description: "Aufgaben und Termine",
    group: "Kunden und Vertrieb",
    feature: "crm",
  },
  {
    key: "marketing",
    label: "Marketing",
    description: "Kampagnen vorbereiten",
    group: "Kunden und Vertrieb",
    feature: "marketing",
  },
  {
    key: "projekte",
    label: "Projekte",
    description: "Gemeinsame Arbeit planen",
    group: "Zusammenarbeiten",
    feature: "projects",
  },
  {
    key: "dateiexplorer",
    label: "Dateiexplorer",
    description: "Ordner und Dateien",
    group: "Zusammenarbeiten",
  },
  {
    key: "wissen",
    label: "Wissensdatenbank",
    description: "Wissen finden und pflegen",
    group: "Zusammenarbeiten",
    feature: "knowledge",
  },
  {
    key: "lernen",
    label: "Lernen",
    description: "Kurse und Lernfortschritt",
    group: "Zusammenarbeiten",
    feature: "learning",
  },
  {
    key: "bem",
    label: "BEM",
    description: "Geschützte Fallakten und nächste Schritte",
    group: "Zusammenarbeiten",
    feature: "bem",
  },
  {
    key: "finanzen",
    label: "Finanzen",
    description: "Belege und Liquidität",
    group: "Auswerten und verwalten",
    feature: "finance",
  },
  {
    key: "befragungen",
    label: "Befragungen",
    description: "Ergebnisse und Freigaben",
    group: "Auswerten und verwalten",
    feature: "surveys",
  },
  {
    key: "berichte",
    label: "Berichte",
    description: "Kennzahlen und PDF-Berichte",
    group: "Auswerten und verwalten",
    feature: "reports",
  },
  {
    key: "verwaltung",
    label: "Verwaltung",
    description: "Zugänge und Einstellungen",
    group: "Auswerten und verwalten",
    feature: "admin",
  },
];

export function visibleHelpSections(features: Record<string, boolean> = {}) {
  return helpSections.filter((section) =>
    section.key === "bem"
      ? features.bem === true || (features.admin === true && features.bemManage === true)
      : !section.feature || features[section.feature] === true,
  );
}

export function helpPath(section: HelpSectionKey): string {
  return section === "start" ? "/hilfe" : `/hilfe?bereich=${section}`;
}

export function helpSectionForPath(pathname: string): HelpSectionKey {
  if (pathname === "/administration/bem") return "bem";
  if (pathname === "/bem" || pathname.startsWith("/bem/")) return "bem";
  if (pathname.startsWith("/dateiexplorer")) return "dateiexplorer";
  if (
    pathname.startsWith("/crm/vertrieb") ||
    pathname.startsWith("/crm/leads") ||
    pathname.startsWith("/crm/deals")
  ) {
    return "vertrieb";
  }
  if (pathname.startsWith("/crm/kunden") || pathname.startsWith("/crm/organisationen")) {
    return "kunden";
  }
  if (pathname.startsWith("/crm/kontakte")) return "ansprechpartner";
  if (pathname.startsWith("/crm/angebote")) return "angebote";
  if (
    pathname.startsWith("/crm/kommunikation") ||
    pathname.startsWith("/crm/e-mail") ||
    pathname.startsWith("/crm/anrufe") ||
    pathname.startsWith("/einstellungen/e-mail")
  )
    return "kommunikation";
  if (pathname.startsWith("/crm/dokumente")) return "dokumente";
  if (pathname.startsWith("/crm/aktivitaeten")) return "aktivitaeten";
  if (pathname.startsWith("/projekte")) return "projekte";
  if (pathname.startsWith("/finanzen")) return "finanzen";
  if (pathname.startsWith("/marketing")) return "marketing";
  if (pathname.startsWith("/lernen")) return "lernen";
  if (pathname.startsWith("/befragungen")) return "befragungen";
  if (pathname.startsWith("/wissen")) return "wissen";
  if (pathname.startsWith("/berichte")) return "berichte";
  if (pathname.startsWith("/administration") || pathname === "/dokumentation") return "verwaltung";
  return "start";
}
